import { alpha, createTheme, CssBaseline, responsiveFontSizes, ThemeProvider } from "@material-ui/core";
import { useSelector } from "react-redux";
import GlobalStyles from "src/configs/GlobalStyles";

const round = (value) => Math.round(value * 1e5) / 1e5;
const pxToRem = (size) => `${size / 16}rem`;
const buildVariant = (fontWeight, size, lineHeight, letterSpacing) => ({
  fontWeight,
  fontSize: pxToRem(size),
  lineHeight: `${round(lineHeight / size)}`,
  ...(letterSpacing !== undefined ? { letterSpacing: `${round(letterSpacing / size)}em` } : {}),
});

export default function ThemeWrapper({ children }) {
  const isDarkMode = useSelector((state) => state.themeSlice.isDarkMode);

  const theme = createTheme({
    palette: {
      type: isDarkMode ? "dark" : "light",
      background: {
        paper: isDarkMode ? "#001229" : "#FFFFFF",
        primary: isDarkMode ? "#001229" : "#FFFFFF",
        secondary: isDarkMode ? "#16243B" : "#FFFFFF",
        primaryGradient: "linear-gradient(100.42deg, #2C85EE 16.07%, #4FB5FF 79.2%)",
      },
      primary: {
        main: isDarkMode ? "#1998F4" : "#2C85EE",
      },
      secondary: {
        main: isDarkMode ? "#ffffffb3" : "#EE7565",
      },
      custom: {
        Table: {
          rowStripColor: isDarkMode ? "#100F36" : "#F6F8FF",
          indicatorColor: "#7C61E0",
        },
        Paper: {
          titleColor: isDarkMode ? "#727CF5" : "#6D7882",
          valueColor: isDarkMode ? "#FFFFFF" : "#7C61E0",
        },
        Skeleton: {
          bg: isDarkMode ? "#060931" : "#BBBBBB",
          aniBg: isDarkMode
            ? "linear-gradient(90deg, #060931, #20244E, #060931)"
            : "linear-gradient(90deg, #BBBBBB, #FFFFFF, #BBBBBB)",
        },
      },
    },
    breakpoints: {
      values: { xs: 0, sm: 760, md: 960, lg: 1280, xl: 1440, xxl: 1800 },
    },
    typography: {
      h1: buildVariant(700, 30, 35.16),
      h2: buildVariant(500, 30, 35.16),
      h3: buildVariant(700, 20, 23.44, 0.25),
      h4: buildVariant(500, 20, 23.44, 0.15),
      h5: buildVariant(500, 18, 20, 0.15),
      h6: buildVariant(400, 18, 20),
      body1: buildVariant(400, 16, 18.75, 0.15),
      body2: buildVariant(300, 16, 18.75, 0.15),
      subtitle1: buildVariant(700, 16, 18.75, 0.15),
      subtitle2: buildVariant(500, 16, 18.75, 0.15),
      button: {
        ...buildVariant(500, 14, 24, 0.15),
        textTransform: "none",
      },
    },
    overrides: {
      MuiCssBaseline: {
        "@global": {
          "*": {
            boxSizing: "border-box",
            margin: 0,
            padding: 0,
          },
          html: {
            "-webkit-font-smoothing": "antialiased",
            "-moz-osx-font-smoothing": "grayscale",
          },
          body: {
            fontFamily: "Roboto",
          },
          ".second-font": {
            fontFamily: "Roboto",
          },
          "#root": {
            height: "100%",
            width: "100%",
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": {
              width: 2,
            },
            "&::-webkit-scrollbar:horizontal": {
              height: 2,
            },
            "&::-webkit-scrollbar-thumb, &::-webkit-scrollbar-corner": {
              backgroundColor: alpha("#FFFFFF", 0.65),
            },
          },
        },
      },
      MuiTableHead: {
        root: {
          "& .MuiTableRow-root": {
            backgroundColor: "#001229",
            "& .MuiTableCell-root": {
              border: "none",
            },
            "& .MuiTypography-root": {
              fontSize: "14px",
              fontWeight: 500,
            },
          },
        },
      },
      MuiTableBody: {
        root: {
          "& .MuiTableRow-root": {
            cursor: "pointer",
            "& .MuiTableCell-root": {
              border: "none",
            },
          },
        },
      },
    },
    props: {
      MuiPopover: {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        transformOrigin: {
          vertical: "top",
          horizontal: "center",
        },
      },
      MuiTooltip: {
        placement: "top",
      },
      MuiPagination: {
        shape: "rounded",
        color: "primary",
      },
      MuiContainer: {
        maxWidth: "lg",
      },
    },
  });

  return (
    <ThemeProvider theme={responsiveFontSizes(theme)}>
      <GlobalStyles />
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}
